import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  TextField,
  Button,
  Box,
  Stack,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Typography,
  LinearProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import apiService from "../apiService/ApiService";
import { ForgotPasswordApi } from "../utils/urlConst";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../components/CommonLoader";
import { useAlert } from "../contexts/AlertContext";
import AppLogoContainer from "../components/UserModule/AppLogoContainer";
import useWindowDimensions from "../hooks/useWindowDimensions";
import { useTranslation } from "react-i18next";
import CommonTextField from "../components/CommonTextField";
import useResponsive from "../hooks/useResponsive";
import CommonButton from "../components/CommonButton";
import userIcon from "../assets/user.svg";
import passwordIcon from "../assets/password.svg";

interface FormData {
  email: string;
  newPassword: string;
  confirmPassword: string;
}

const PasswordResetForm: React.FC = () => {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    trigger,
  } = useForm<FormData>();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { isMobile, isTablet } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const windowHeight = useWindowDimensions().windowHeight;

  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const { t } = useTranslation();

  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    if (!token) {
      showAlert("Invalid reset token", "error");
      navigate("/resetpasswordrequest");
    }
  }, [token, showAlert, navigate]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setErrorMessage("");
    try {
      let jsondata = JSON.stringify({
        token: token,
        email: data.email.trim(),
        password: data.newPassword,
        password_confirmation: data.confirmPassword,
      });

      const responseJson = await apiService.post<any>(
        ForgotPasswordApi,
        jsondata
      );

      if (
        responseJson?.hasOwnProperty("status_code") &&
        responseJson?.status_code === 1
      ) {
        showAlert(t("resetpassword.changed_sucess"), "success");
        navigate("/login");
      } else if (
        responseJson?.hasOwnProperty("status_code") &&
        responseJson?.status_code === 0
      ) {
        setErrorMessage(t("resetpassword.email_does_not_exist"));
      } else if (
        responseJson?.hasOwnProperty("status_code") &&
        responseJson?.status_code === 2
      ) {
        showAlert(t("resetpassword.invalid_token"), "error");
      } else {
        showAlert(t("common.server_error"), "error");
      }
    } catch (error) {
      showAlert(t("common.server_error"), "error");
    } finally {
      setLoading(false);
    }
  };

  const newPassword = watch("newPassword");

  // const validatePassword = (value: string) => {
  //   let msg = "";
  //   if (value.length < 8) msg = "Password must be at least 8 characters";
  //   if (!/[a-z]/.test(value))
  //     msg += "Password must contain at least 1 lowercase letter";
  //   if (!/[A-Z]/.test(value))
  //     msg += "Password must contain at least 1 uppercase letter";
  //   if (!/[0-9]/.test(value)) msg += "Password must contain at least 1 number";
  //   if (!/[!@#$%^&*]/.test(value))
  //     msg += "Password must contain at least 1 special character";
  //   return msg;
  // };
  const validatePassword = (value: string): string => {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    return passwordRegex.test(value)
      ? ""
      : t("resetpassword.password_format_error");
  };
  const isValidPassword = (value: string) => {
    if (
      value.length >= 8 &&
      /[a-z]/.test(value) &&
      /[A-Z]/.test(value) &&
      /[0-9]/.test(value) &&
      /[!@#$%^&*]/.test(value)
    ) {
      return true;
    }
    return false;
  };

  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*]/.test(password)) strength++;
    return (strength / 5) * 100;
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    const error = validatePassword(value);
    setPasswordError(error);
    setPasswordStrength(getPasswordStrength(value));
    trigger("newPassword");
  };
  useEffect(() => {}, [errors]);

  return (
    <Box
      sx={{
        height: isMobile ? "auto" : windowHeight,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        overflow: "auto",
      }}
    >
      <CommonLoader isVisible={loading} />
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems="center"
        justifyContent={isMobile ? "flex-start" : "space-evenly"}
        sx={{
          width: "100%",
          height: isMobile ? "100%" : isTablet ? "auto" : 350,
        }}
      >
        <AppLogoContainer />
        <Box
          sx={{
            width: isMobile ? "90%" : isTablet ? "50%" : "30%",
            mt: isMobile || isTablet ? "80px" : "0px",
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            <Stack spacing={"18px"}>
              <Typography
                fontFamily={"Nunito Sans Bold"}
                sx={{
                  mt: "38px",
                  color: "#2B2A5C",
                  alignSelf: "center",
                  fontSize: "20px",
                }}
              >
                {t("resetpassword.Reset_Password")}
              </Typography>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                rules={{
                  required: t("login.Email_is_required"),
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: t("resetpassword.Email_is_invalid"),
                  },
                }}
                render={({ field }) => (
                  <CommonTextField
                    {...field}
                    placeholder={t("login.Email")}
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    icon={userIcon}
                    name={"user"}
                    onChange={(e) => field.onChange(e.target.value.trim())}
                    fontSize={"12px"}
                  />
                )}
              />
              <Controller
                name="newPassword"
                control={control}
                defaultValue=""
                rules={{
                  required: t("resetpassword.New_Password_is_required"),
                  validate: (value) =>
                    isValidPassword(value) || validatePassword(value),
                }}
                render={({ field }) => (
                  <CommonTextField
                    {...field}
                    placeholder={t("resetpassword.placeholder_newPassword")}
                    fullWidth
                    error={!!errors.newPassword}
                    helperText={
                      errors.newPassword ? errors.newPassword.message : ""
                    }
                    icon={passwordIcon}
                    name={"Newpassword"}
                    fontSize={"12px"}
                    isPassword={true}
                    onChange={(e) => {
                      field.onChange(e);
                      handlePasswordChange(e);
                    }}
                  />
                )}
              />
              <Controller
                name="confirmPassword"
                control={control}
                defaultValue=""
                rules={{
                  required: t("resetpassword.Confirm_Password_is_required"),
                  validate: (value) =>
                    value === newPassword ||
                    t("resetpassword.Passwords_must_match"),
                }}
                render={({ field }) => (
                  <CommonTextField
                    {...field}
                    placeholder={t("resetpassword.placeholder_confirmPassword")}
                    fullWidth
                    error={!!errors.confirmPassword}
                    helperText={
                      errors.confirmPassword
                        ? errors.confirmPassword.message
                        : ""
                    }
                    icon={passwordIcon}
                    name={"confirmpassword"}
                    fontSize={"12px"}
                    isPassword={true}
                  />
                )}
              />
              {errorMessage && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "error.main",
                    mt: 1,
                    mb: 2,
                  }}
                >
                  {errorMessage}
                </Typography>
              )}
              <CommonButton
                translationKey={"resetpassword.reset_button"}
                type="submit"
              />
            </Stack>
          </form>
          <Typography
            fontSize={17}
            tabIndex={0}
            sx={{
              my: 3,
              textAlign: "center",
              color: "grey",
            }}
          >
            <span
              onClick={() => navigate("/login")}
              style={{
                padding: '2px 4px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onMouseOver={e => (e.currentTarget.style.color = theme.palette.primary.main)}
              onMouseOut={e => (e.currentTarget.style.color = 'grey')}
              onFocus={e => (e.currentTarget.style.color = theme.palette.primary.main)}
              onBlur={e => (e.currentTarget.style.color = 'grey')}
            >
              {t("resetpassword.back_to_login_button")}
            </span>
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

export default PasswordResetForm;
